/** Short-lived user/route selection wins over stale registry GET activeBaseId. */

const PIN_PREFIX = "atw-one.knowledge.activePin";

export const KNOWLEDGE_ACTIVE_PIN_MS = 20_000;

type ActivePin = { id: string; atMs: number };

function orgKey(orgId: string | null | undefined): string {
  return (orgId || "default").trim() || "default";
}

function pinStorageKey(orgId: string | null | undefined): string {
  return `${PIN_PREFIX}.${orgKey(orgId)}`;
}

export function pinKnowledgeBaseActiveSelection(
  orgId: string | null | undefined,
  baseId: string,
): void {
  if (typeof window === "undefined") return;
  const id = baseId.trim();
  if (!id) return;
  const pin: ActivePin = { id, atMs: Date.now() };
  try {
    window.localStorage.setItem(pinStorageKey(orgId), JSON.stringify(pin));
  } catch {
    /* ignore */
  }
}

export function getKnowledgeBaseActivePin(
  orgId: string | null | undefined,
): ActivePin | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(pinStorageKey(orgId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActivePin;
    if (!parsed || typeof parsed.id !== "string" || typeof parsed.atMs !== "number") {
      return null;
    }
    const id = parsed.id.trim();
    if (!id) return null;
    return { id, atMs: parsed.atMs };
  } catch {
    return null;
  }
}

export function clearKnowledgeBaseActivePin(orgId: string | null | undefined): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(pinStorageKey(orgId));
  } catch {
    /* ignore */
  }
}

/** Drop pin once the server active id matches the pinned selection. */
export function reconcileKnowledgeBaseActivePin(
  orgId: string | null | undefined,
  serverActiveId: string,
): void {
  const pin = getKnowledgeBaseActivePin(orgId);
  if (!pin) return;
  if (pin.id === (serverActiveId || "").trim()) {
    clearKnowledgeBaseActivePin(orgId);
  }
}

export function pinnedKnowledgeBaseActiveId(
  orgId: string | null | undefined,
  isValidId: (id: string) => boolean,
): string {
  const pin = getKnowledgeBaseActivePin(orgId);
  if (!pin) return "";
  if (Date.now() - pin.atMs > KNOWLEDGE_ACTIVE_PIN_MS) return "";
  if (!isValidId(pin.id)) return "";
  return pin.id;
}
